/**
 * FauxDS Avatar - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Avatar component for prototyping.
 * It is NOT the real @faire/slate Avatar and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Avatar, see: @faire/slate/components/avatar
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 43967:515 (Slate Avatar)
 * - Design tokens extracted from Figma variables
 * - All variants verified: XXSmall, XSmall, Small, Medium, Large, XLarge
 * - Formats verified: Image, Label
 * - Uses Faire Black (#333333) as background color for blank avatars (initials only)
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import clsx from "clsx";
import React, { useState, forwardRef } from "react";

export type AvatarSize =
  | "xxSmall"
  | "xSmall"
  | "small"
  | "medium"
  | "large"
  | "xLarge";
export type AvatarFormat = "Image" | "Label";

export interface AvatarProps {
  /**
   * Icon props to display (just use an empty object to signal that an icon should be displayed)
   * @default undefined
   */
  iconProps?: {};
  /**
   * The label text to display (first letter used for initials)
   * @default "F"
   */
  label?: string;
  /**
   * The color of the border
   * @default undefined
   */
  borderColor?: string;
  /**
   * Avatar size
   * @default "xxSmall"
   */
  size?: AvatarSize;
  /**
   * Image object
   *
   * ```ts
   * {
   *   url: "https://some.image.url",
   *   alt_text: "Some image alt text",
   * }
   * ```
   */
  image?: { url: string; alt_text: string };

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

// Size to pixel mapping (from Figma dimensions)
const sizeMap: Record<AvatarSize, number> = {
  xxSmall: 32, // --slate-dimensions-8
  xSmall: 40, // --slate-dimensions-10
  small: 48, // --slate-dimensions-12
  medium: 56, // --slate-dimensions-14
  large: 64, // --slate-dimensions-16
  xLarge: 96, // Calculated from design
};

// Size to font size mapping for initials (proportional to avatar size)
const fontSizeMap: Record<AvatarSize, string> = {
  xxSmall: "text-[12px]",
  xSmall: "text-[14px]",
  small: "text-[16px]",
  medium: "text-[20px]",
  large: "text-[24px]",
  xLarge: "text-[36px]",
};

/**
 * Avatar - FauxDS (recreated from Figma MCP)
 *
 * An avatar acts as a proxy for a user or entity (such as a project, repository, or space).
 * They're often combined with status indicators to give more context. Users generally upload
 * their own image, otherwise, initials are displayed by default.
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      iconProps: icon = false,
      label: labelText = "F",
      borderColor,
      size = "xxSmall",
      image,
      className,
    },
    ref
  ) => {
    const [imageError, setImageError] = useState(false);

    const pixelSize = sizeMap[size];
    const fontSize = fontSizeMap[size];
    const showImage = image?.url && !imageError;
    const initial = labelText?.charAt(0).toUpperCase() || "F";
    const ariaLabel = labelText || image?.alt_text;

    const containerClasses = clsx(
      "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full",
      !showImage ? "bg-[var(--slate-color-surface-primary-inverse)]" : "",
      borderColor ? "ring-2 ring-(--avatar-border-color) ring-offset-2" : "",
      className
    );

    const borderColorStyle = borderColor
      ? { "--avatar-border-color": borderColor }
      : {};

    return (
      <div
        ref={ref}
        className={containerClasses}
        style={{
          width: pixelSize,
          height: pixelSize,
          minWidth: pixelSize,
          borderRadius: "var(--slate-radius-component-round)",
          ...borderColorStyle,
        }}
        role="img"
        aria-label={ariaLabel}
      >
        {showImage ? (
          <img
            src={image?.url}
            alt={image?.alt_text || ariaLabel}
            onError={() => setImageError(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <span
            className={cn(
              "font-medium text-[var(--slate-color-text-primary-inverse)] select-none",
              fontSize
            )}
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontWeight: "var(--slate-typography-weight-medium)",
            }}
          >
            {initial}
          </span>
        )}

        {/* Icon indicator (for event participation) */}
        {icon && (
          <div
            className="absolute right-0 bottom-0 flex items-center justify-center rounded-full bg-[var(--slate-color-action-surface-default)]"
            style={{
              width: `${pixelSize * 0.375}px`,
              height: `${pixelSize * 0.375}px`,
            }}
          >
            {/* Placeholder for icon - would be replaced with actual icon component */}
            <div
              className="h-2 w-2 rounded-full bg-[var(--slate-color-action-icon-inverse)]"
              style={{
                width: `${pixelSize * 0.25}px`,
                height: `${pixelSize * 0.25}px`,
              }}
            />
          </div>
        )}
      </div>
    );
  }
);

Avatar.displayName = "Avatar";

export default Avatar;
